// WhatIs N×5 board with GPU-instanced tiles (single-round mode, Apr 2026).
// Category headers live on the top row as N textured plane meshes (low count → no instancing).
// The N*5 clue tiles share one InstancedMesh; we rewrite per-instance matrices + colors to
// animate the board-reveal stagger without per-tile draw calls.
//
// Column count is per-game (3 or 6, streamer-configurable). The board is
// rebuilt at every populateBoard() call, sizing tiles so the overall board
// width stays ~constant (~1200px) regardless of column count — viewers see
// the same stage envelope, just chunkier or thinner cards.
//
// Coord system: origin at board center; up=+Y, right=+X; measured in pixels.

import {
  Color,
  DoubleSide,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Quaternion,
  Vector3,
  type Texture,
} from 'three';

import { clamp01, easeOutCubic } from './lib/easing.js';
import { flipFrameAt } from './tile-flip.js';
import {
  CARD_RADIUS,
  CARD_SHADOW_BLUR,
  CARD_SHADOW_COLOR,
  CARD_SHADOW_OFFSET_Y,
  COLOR_STAGE_BLUE,
  FONT_DISPLAY,
  FONT_OSWALD,
} from './lib/theme.js';
import { textTexture } from './lib/text-texture.js';
import { getThemeVar } from '../ui/theme.js';

/** Hex → 0xRRGGBB number for Three.Color. */
function hexToInt(hex: string, fallback: number): number {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex);
  return m ? parseInt(m[1]!, 16) : fallback;
}

/**
 * Read tile background + text colors from CSS custom properties (set at boot
 * by applyTheme). Three.js materials can't consume CSS vars directly, so we
 * snapshot the values at scene init; the streamer changes theme rarely
 * enough that live-updating Three materials isn't worth the complexity.
 */
function readBoardPalette(): {
  tile: number;
  tileDim: number;
  tileText: string;
  header: string;
  headerText: string;
} {
  const tileHex = getThemeVar('tile_bg', '#060CE9');
  const primaryHex = getThemeVar('primary', '#FFC43D');
  // Dim version for consumed tiles: halve brightness. 0x80 mask approximates
  // the original COLOR_STAGE_BLUE_DIM behaviour for the default palette.
  const tileInt = hexToInt(tileHex, COLOR_STAGE_BLUE);
  const tileDim = ((tileInt >> 1) & 0x7f7f7f) | 0x202020;
  return {
    tile: tileInt,
    tileDim,
    tileText: primaryHex,
    header: tileHex,
    headerText: '#FFFFFF',
  };
}

/**
 * Maximum supported column count. Pre-allocated buffer sizes (instanced mesh
 * count, overlay mesh array length) use this so we can rebuild for fewer
 * columns without re-allocating Three.js objects on every populateBoard.
 */
export const BOARD_COLS_MAX = 6;
/** Default column count for callers that don't yet know the per-game width. */
export const BOARD_COLS_DEFAULT = 6;
/**
 * Backwards-compat alias kept for code that imported the old `BOARD_COLS`
 * constant. New code should read the live count off `setContent`'s input.
 */
export const BOARD_COLS = BOARD_COLS_MAX;
export const BOARD_ROWS = 5;
export const TILE_COUNT_MAX = BOARD_COLS_MAX * BOARD_ROWS;

// Tile dimensions are recomputed per layout so the overall board WIDTH stays
// roughly the same (~1200px) regardless of column count. With 3 cols we get
// chunkier ~360-wide tiles; with 6 we get ~190-wide tiles. Height is fixed so
// row positions don't change shape between the two modes.
export const TILE_H = 130;
export const TILE_GAP = 12;
export const HEADER_H = 120;
/** Target overall board width — drives per-column TILE_W via tileWidthFor(). */
export const BOARD_TARGET_W = 1200;

/** Compute per-column tile width so total board width = BOARD_TARGET_W. */
export function tileWidthFor(cols: number): number {
  if (cols <= 0) return 0;
  // Solve for W in: cols*W + (cols-1)*GAP = TARGET_W.
  return Math.round((BOARD_TARGET_W - (cols - 1) * TILE_GAP) / cols);
}

export interface BoardHandle {
  group: Object3D;
  /** Replace the 5 categories + 5 dollar ticks shown on the board. */
  setContent: (categories: string[], values: number[]) => void;
  /** Visually consume a tile (dim it / hide it) once its clue has been played. */
  consumeTile: (col: number, row: number) => void;
  /** Set all 30 tiles back to the idle state. */
  resetTiles: () => void;
  /** Kick off the staggered board-reveal animation. Duration ~2.5s per GDD. */
  playReveal: () => void;
  /**
   * Jeopardy-style "I'll take SCIENCE for 800" — pulse + flip + zoom the
   * tile at (col, row). Fires one animation; auto-resolves the returned
   * promise when the zoom is at full size so the clue-zoom scene can take
   * over. Safe to call repeatedly; a second invocation before the first
   * completes cancels the prior animation.
   */
  flipTile: (col: number, row: number) => Promise<void>;
  /** Best-effort column lookup for a category name (case-insensitive). -1 if absent. */
  columnOfCategory: (category: string) => number;
  /** Row lookup for a dollar value (matches the values passed to setContent). -1 if absent. */
  rowOfValue: (value: number) => number;
  /** Per-frame tick; pass performance.now() in ms. */
  tick: (now: number) => void;
  dispose: () => void;
}

interface TileState {
  consumed: boolean;
}

export function createBoard(): BoardHandle {
  const group = new Object3D();
  group.name = 'board';

  // Snapshot the theme palette. Read once at scene init — the streamer sets
  // this before a game and we don't want mid-scene material churn.
  const palette = readBoardPalette();

  // Live geometry: cols + per-tile width are mutable so populateBoard() can
  // adapt to the streamer's 3-vs-6 setting. Pre-allocate buffers + meshes for
  // the MAX (6) and just hide the tail when running 3-wide. This avoids
  // per-round Three.js object churn while keeping the API tiny.
  let cols = BOARD_COLS_DEFAULT;
  let tileW = tileWidthFor(cols);
  let tileCount = cols * BOARD_ROWS;

  // Tile instanced mesh — sized for the MAX column count so we never have to
  // recreate it. Unused tail instances render zero-scale (effectively gone).
  const tileGeom = new PlaneGeometry(1, TILE_H);
  const tileMat = new MeshBasicMaterial({
    color: 0xffffff, // multiplied against per-instance color attribute
    side: DoubleSide,
    transparent: true,
  });
  const instanced = new InstancedMesh(tileGeom, tileMat, TILE_COUNT_MAX);
  instanced.frustumCulled = false;
  instanced.name = 'board-tiles';
  // Tile backgrounds are now drawn (with rounded corners + drop shadow) inside
  // the per-tile overlay mesh via textTexture(bg=...). The instanced mesh
  // remains for positioning + consumed-state dim opacity, but its own fill is
  // hidden so square edges don't poke out from under the rounded overlay.
  tileMat.opacity = 0;
  tileMat.depthWrite = false;

  // Per-instance color buffer (tile-bg from theme, dimmable on consume).
  const colorArr = new Float32Array(TILE_COUNT_MAX * 3);
  const tileBaseColor = new Color(palette.tile);
  for (let i = 0; i < TILE_COUNT_MAX; i++) {
    colorArr[i * 3 + 0] = tileBaseColor.r;
    colorArr[i * 3 + 1] = tileBaseColor.g;
    colorArr[i * 3 + 2] = tileBaseColor.b;
  }
  instanced.instanceColor = new InstancedBufferAttribute(colorArr, 3);
  group.add(instanced);

  // Per-tile text overlays (dollar amounts). Pre-allocate at MAX; geometry
  // gets rebuilt on column-count change so the overlay matches tileW exactly.
  const tileTextMeshes: Mesh[] = [];
  for (let i = 0; i < TILE_COUNT_MAX; i++) {
    const g = new PlaneGeometry(tileW, TILE_H);
    const m = new MeshBasicMaterial({ transparent: true, depthWrite: false });
    const mesh = new Mesh(g, m);
    mesh.renderOrder = 1;
    tileTextMeshes.push(mesh);
    group.add(mesh);
  }

  // Category headers — pre-allocated at MAX, hidden when col count is lower.
  const headerMeshes: Mesh[] = [];
  for (let c = 0; c < BOARD_COLS_MAX; c++) {
    const g = new PlaneGeometry(tileW, HEADER_H);
    const m = new MeshBasicMaterial({ transparent: true });
    const mesh = new Mesh(g, m);
    headerMeshes.push(mesh);
    group.add(mesh);
  }

  const tileState: TileState[] = Array.from({ length: TILE_COUNT_MAX }, () => ({ consumed: false }));
  const dummy = new Object3D();
  const tmpMat = new Matrix4();
  const tmpPos = new Vector3();
  const tmpQuat = new Quaternion();
  const tmpScale = new Vector3(1, 1, 1);

  let revealStartMs: number | null = null;
  const revealDuration = 2500;
  const perTileDuration = 650;

  // Current categories + values, tracked so the board can answer
  // columnOfCategory/rowOfValue lookups without the caller threading them
  // through manually. setContent() writes to both on every round change.
  let currentCategories: string[] = [];
  let currentValues: number[] = [];

  // Flip-tile state. One tile animates at a time; see tile-flip.ts for the
  // per-frame transform math.
  let flipStartMs: number | null = null;
  let flipCol = -1;
  let flipRow = -1;
  let flipResolve: (() => void) | null = null;

  function setInstance(i: number, x: number, y: number, scale: number, rotX: number): void {
    dummy.position.set(x, y, 0);
    dummy.rotation.set(rotX, 0, 0);
    dummy.scale.setScalar(scale);
    dummy.updateMatrix();
    instanced.setMatrixAt(i, dummy.matrix);
  }

  function setColor(i: number, color: Color): void {
    instanced.setColorAt(i, color);
  }

  function applyIdleMatrix(i: number, col: number, row: number): void {
    const { x, y } = tilePositionFor(col, row, cols, tileW);
    setInstance(i, x, y, 1, 0);
    const overlay = tileTextMeshes[i];
    if (overlay) overlay.position.set(x, y, 0.1);
  }

  /** Hide instances + overlays beyond the active column count (3-col mode). */
  function hideTail(): void {
    for (let i = tileCount; i < TILE_COUNT_MAX; i++) {
      setInstance(i, 0, 0, 0, 0);
      const overlay = tileTextMeshes[i];
      if (overlay) overlay.visible = false;
    }
    for (let c = cols; c < BOARD_COLS_MAX; c++) {
      const h = headerMeshes[c];
      if (h) h.visible = false;
    }
  }

  function showActive(): void {
    for (let i = 0; i < tileCount; i++) {
      const overlay = tileTextMeshes[i];
      if (overlay) overlay.visible = true;
    }
    for (let c = 0; c < cols; c++) {
      const h = headerMeshes[c];
      if (h) h.visible = true;
    }
  }

  function layoutAll(): void {
    showActive();
    for (let row = 0; row < BOARD_ROWS; row++) {
      for (let col = 0; col < cols; col++) {
        const i = row * cols + col;
        applyIdleMatrix(i, col, row);
      }
    }
    for (let c = 0; c < cols; c++) {
      const header = headerMeshes[c];
      if (header) {
        const { x, y } = tilePositionFor(c, -1, cols, tileW);
        header.position.set(x, y, 0);
      }
    }
    hideTail();
    instanced.instanceMatrix.needsUpdate = true;
  }
  layoutAll();

  /**
   * Resize per-tile geometry when the column count changes. Three.js geometry
   * is immutable in dimensions; cheapest correct path is dispose + replace.
   * Only fires when cols actually changes (typically once per game).
   */
  function rebuildGeometryFor(newCols: number): void {
    cols = newCols;
    tileW = tileWidthFor(cols);
    tileCount = cols * BOARD_ROWS;
    for (const m of tileTextMeshes) {
      m.geometry.dispose();
      m.geometry = new PlaneGeometry(tileW, TILE_H);
    }
    for (const h of headerMeshes) {
      h.geometry.dispose();
      h.geometry = new PlaneGeometry(tileW, HEADER_H);
    }
    layoutAll();
  }

  function setContent(categories: string[], values: number[]): void {
    // Column count is implicit in the categories array length — backend slices
    // pickRoundCategories() to the per-tenant categories_per_round (3 or 6).
    // Anything outside [1, BOARD_COLS_MAX] is clamped so a malformed snapshot
    // can't crash the scene.
    const desired = Math.max(
      1,
      Math.min(BOARD_COLS_MAX, categories.length || BOARD_COLS_DEFAULT),
    );
    if (desired !== cols) rebuildGeometryFor(desired);
    currentCategories = categories.slice(0, cols);
    currentValues = values.slice(0, BOARD_ROWS);
    for (let c = 0; c < cols; c++) {
      const cat = (categories[c] ?? '').toUpperCase();
      const tex = textTexture(cat, {
        font: `42px ${FONT_OSWALD}`,
        color: palette.headerText,
        bg: palette.header,
        width: tileW,
        height: HEADER_H,
        weight: 'bold',
        paddingX: 10,
        borderRadius: CARD_RADIUS,
        shadowColor: CARD_SHADOW_COLOR,
        shadowBlur: CARD_SHADOW_BLUR,
        shadowOffsetY: CARD_SHADOW_OFFSET_Y,
        autoFit: true,
        minFontPx: 18,
      });
      const header = headerMeshes[c];
      if (header) assignTexture(header, tex);
    }
    for (let row = 0; row < BOARD_ROWS; row++) {
      const v = values[row] ?? 0;
      for (let col = 0; col < cols; col++) {
        const i = row * cols + col;
        const tex = textTexture(`${v}`, {
          font: `56px ${FONT_DISPLAY}`,
          color: palette.tileText,
          bg: palette.tile === 0x060ce9 ? '#060CE9' : toHex(palette.tile),
          width: tileW,
          height: TILE_H,
          weight: 'bold',
          stroke: '#000000',
          strokeWidth: 3,
          paddingX: 8,
          borderRadius: CARD_RADIUS,
          shadowColor: CARD_SHADOW_COLOR,
          shadowBlur: CARD_SHADOW_BLUR,
          shadowOffsetY: CARD_SHADOW_OFFSET_Y,
        });
        const overlay = tileTextMeshes[i];
        if (overlay) assignTexture(overlay, tex);
      }
    }
  }

  /** Convert an integer 0xRRGGBB into a '#RRGGBB' hex string. */
  function toHex(n: number): string {
    return '#' + n.toString(16).padStart(6, '0').toUpperCase();
  }

  function consumeTile(col: number, row: number): void {
    const i = row * cols + col;
    const state = tileState[i];
    if (!state) return;
    state.consumed = true;
    setColor(i, new Color(palette.tileDim));
    if (instanced.instanceColor) instanced.instanceColor.needsUpdate = true;
    const overlay = tileTextMeshes[i];
    if (overlay) (overlay.material as MeshBasicMaterial).opacity = 0.15;
  }

  function resetTiles(): void {
    for (let i = 0; i < tileCount; i++) {
      const state = tileState[i];
      if (state) state.consumed = false;
      setColor(i, new Color(palette.tile));
      const overlay = tileTextMeshes[i];
      if (overlay) (overlay.material as MeshBasicMaterial).opacity = 1;
    }
    if (instanced.instanceColor) instanced.instanceColor.needsUpdate = true;
  }

  function playReveal(): void {
    revealStartMs = performance.now();
    // Start each tile from above and scaled down.
    for (let i = 0; i < tileCount; i++) {
      const { x } = getColRowXY(i);
      setInstance(i, x, 1200, 0.2, 0);
      const overlay = tileTextMeshes[i];
      if (overlay) {
        overlay.visible = false;
      }
    }
    instanced.instanceMatrix.needsUpdate = true;
  }

  function tick(now: number): void {
    tickReveal(now);
    tickFlip(now);
  }

  function tickReveal(now: number): void {
    if (revealStartMs === null) return;
    const elapsed = now - revealStartMs;
    let anyPending = false;
    for (let i = 0; i < tileCount; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const stagger = (row * cols + col) * 30; // wave left-to-right, top-to-bottom
      const t = clamp01((elapsed - stagger) / perTileDuration);
      if (t < 1) anyPending = true;
      const e = easeOutCubic(t);
      const { x, y } = tilePositionFor(col, row, cols, tileW);
      const startY = 1200;
      const curY = startY + (y - startY) * e;
      const scale = 0.4 + 0.6 * e;
      setInstance(i, x, curY, scale, 0);
      const overlay = tileTextMeshes[i];
      if (overlay) {
        overlay.position.set(x, curY, 0.1);
        overlay.visible = t > 0.6;
        (overlay.material as MeshBasicMaterial).opacity = clamp01((t - 0.6) / 0.4);
      }
    }
    instanced.instanceMatrix.needsUpdate = true;
    if (!anyPending && elapsed > revealDuration) {
      revealStartMs = null;
      layoutAll();
    }
  }

  /** Per-frame transform for the one tile currently mid-flip. */
  function tickFlip(now: number): void {
    if (flipStartMs === null) return;
    const i = flipRow * cols + flipCol;
    const overlay = tileTextMeshes[i];
    if (!overlay) return endFlip();
    const { x, y } = tilePositionFor(flipCol, flipRow, cols, tileW);
    const frame = flipFrameAt(now - flipStartMs);
    dummy.position.set(x, y, 0.2);
    dummy.rotation.set(0, frame.rotY, 0);
    dummy.scale.setScalar(frame.scale);
    dummy.updateMatrix();
    instanced.setMatrixAt(i, dummy.matrix);
    instanced.instanceMatrix.needsUpdate = true;
    overlay.position.set(x, y, 0.3);
    overlay.rotation.set(0, frame.rotY, 0);
    overlay.scale.setScalar(frame.scale);
    (overlay.material as MeshBasicMaterial).opacity = frame.textOpacity;
    if (frame.done) endFlip();
  }

  function endFlip(): void {
    flipStartMs = null;
    // Restore idle layout for the flipped tile so the consumed-state dim
    // (which happens later via consumeTile on SCORE_UPDATE) has a clean
    // baseline to paint onto. Overlay orientation resets to 0.
    if (flipCol >= 0 && flipRow >= 0) {
      const i = flipRow * cols + flipCol;
      applyIdleMatrix(i, flipCol, flipRow);
      const overlay = tileTextMeshes[i];
      if (overlay) {
        overlay.rotation.set(0, 0, 0);
        overlay.scale.setScalar(1);
        (overlay.material as MeshBasicMaterial).opacity = 1;
      }
      instanced.instanceMatrix.needsUpdate = true;
    }
    flipCol = -1;
    flipRow = -1;
    const resolve = flipResolve;
    flipResolve = null;
    if (resolve) resolve();
  }

  function flipTile(col: number, row: number): Promise<void> {
    // Cancel any in-flight flip so a second call doesn't produce overlapping
    // transforms on different tiles. The prior promise resolves immediately
    // so its awaiter doesn't hang.
    if (flipStartMs !== null) endFlip();
    flipStartMs = performance.now();
    flipCol = col;
    flipRow = row;
    return new Promise<void>((res) => {
      flipResolve = res;
    });
  }

  function columnOfCategory(category: string): number {
    const norm = category.toUpperCase();
    for (let c = 0; c < currentCategories.length; c++) {
      if ((currentCategories[c] ?? '').toUpperCase() === norm) return c;
    }
    return -1;
  }

  function rowOfValue(value: number): number {
    for (let r = 0; r < currentValues.length; r++) {
      if (currentValues[r] === value) return r;
    }
    return -1;
  }

  function getColRowXY(i: number): { x: number; y: number } {
    const col = i % cols;
    const row = Math.floor(i / cols);
    return tilePositionFor(col, row, cols, tileW);
  }

  void tmpMat;
  void tmpPos;
  void tmpQuat;
  void tmpScale;

  return {
    group,
    setContent,
    consumeTile,
    resetTiles,
    playReveal,
    flipTile,
    columnOfCategory,
    rowOfValue,
    tick,
    dispose() {
      tileGeom.dispose();
      tileMat.dispose();
      for (const m of tileTextMeshes) {
        m.geometry.dispose();
        (m.material as MeshBasicMaterial).dispose();
      }
      for (const h of headerMeshes) {
        h.geometry.dispose();
        (h.material as MeshBasicMaterial).dispose();
      }
    },
  };
}

/**
 * Compute the (x, y) center of a tile for an arbitrary column count + tile
 * width. Origin is board-centered; row=-1 is the header row above row 0.
 * Exported for tests + reuse — the createBoard closure uses it via local cols/tileW.
 */
export function tilePositionFor(
  col: number,
  row: number,
  cols: number,
  tileW: number,
): { x: number; y: number } {
  const boardW = cols * tileW + (cols - 1) * TILE_GAP;
  const boardH = BOARD_ROWS * TILE_H + (BOARD_ROWS - 1) * TILE_GAP;
  const x = col * (tileW + TILE_GAP) - boardW / 2 + tileW / 2;
  const rowFromTop = row + 1; // row -1 -> 0 (topmost)
  const y = boardH / 2 + HEADER_H - rowFromTop * (TILE_H + TILE_GAP) - TILE_H / 2;
  return { x, y };
}

function assignTexture(mesh: Mesh, tex: Texture): void {
  const mat = mesh.material as MeshBasicMaterial;
  if (mat.map && mat.map !== tex) mat.map.dispose();
  mat.map = tex;
  mat.needsUpdate = true;
}
